import "@/App.css";
import { Suspense, lazy, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "@/components/ui/sonner";
import { Layout } from "@/components/layout/Layout";
import { initConversionTracking, trackRouteChange } from "@/lib/analytics";
import { usePremiumFeedback } from "@/hooks/usePremiumFeedback";

function Analytics() {
  const { pathname } = useLocation();
  usePremiumFeedback();
  useEffect(() => { initConversionTracking(); }, []);
  useEffect(() => { trackRouteChange(pathname); }, [pathname]);
  return null;
}

// Customer app features (login, signup, booking, tracking, dashboard, admin)
// live on the ShipOMS app — these ShipKorea URLs hard-redirect there.
const SHIPOMS = {
  login: "https://www.shipoms.in/login",
  register: "https://www.shipoms.in/register",
  book: "https://www.shipoms.in/book",
  track: "https://www.shipoms.in/track",
};

function GoExternal({ to }) {
  useEffect(() => {
    window.location.replace(to);
  }, [to]);
  return (
    <div className="min-h-screen grid place-items-center bg-slate-50 px-6">
      <div className="text-center">
        <p className="text-slate-700 font-medium">Taking you to our booking &amp; tracking app&#8230;</p>
        <a href={to} className="mt-3 inline-block text-korea-blue font-semibold underline">Continue to ShipOMS</a>
      </div>
    </div>
  );
}

const Home = lazy(() => import("@/pages/Home"));
const RoutePage = lazy(() => import("@/pages/RoutePage"));
const Pricing = lazy(() => import("@/pages/Pricing"));
const ServicePage = lazy(() => import("@/pages/ServicePage"));
const FAQ = lazy(() => import("@/pages/FAQ"));
const About = lazy(() => import("@/pages/About"));
const Contact = lazy(() => import("@/pages/Contact"));
const Blog = lazy(() => import("@/pages/Blog"));
const BlogPost = lazy(() => import("@/pages/BlogPost"));
const CityPage = lazy(() => import("@/pages/CityPage"));
const WhyShipKoreaVsEms = lazy(() => import("@/pages/WhyShipKoreaVsEms"));
const KoreaCityPage = lazy(() => import("@/pages/KoreaCityPage"));
const Legal = lazy(() => import("@/pages/Legal"));
const NotFound = lazy(() => import("@/pages/NotFound"));
const Trust = lazy(() => import("@/pages/Trust"));
const KoreanLanding = lazy(() => import("@/pages/KoreanLanding"));
const EcommerceShipping = lazy(() => import("@/pages/EcommerceShipping"));
const KBeautyWholesale = lazy(() => import("@/pages/KBeautyWholesale"));
const KoreaToIndiaCheapest = lazy(() => import("@/pages/KoreaToIndiaCheapest"));
const KoreaToIndiaEcommerce = lazy(() => import("@/pages/KoreaToIndiaEcommerce"));
const KoreaMarketEntry = lazy(() => import("@/pages/KoreaMarketEntry"));
const NorthEastCourier = lazy(() => import("@/pages/NorthEastCourier"));
const NECityPage = lazy(() => import("@/pages/NECityPage"));
const KoMarketEntry = lazy(() => import("@/pages/ko/KoMarketEntry"));
const KoEcommerceIndia = lazy(() => import("@/pages/ko/KoEcommerceIndia"));
const PersonalEffects = lazy(() => import("@/pages/PersonalEffects"));
const DiwaliSweets = lazy(() => import("@/pages/DiwaliSweets"));
const KoBlogParcelGuide = lazy(() => import("@/pages/ko/KoBlogParcelGuide"));
const KoBlogKBeautyExport = lazy(() => import("@/pages/ko/KoBlogKBeautyExport"));
const PersonalCosmetics = lazy(() => import("@/pages/PersonalCosmetics"));
const IndianRiceExport = lazy(() => import("@/pages/IndianRiceExport"));
const AyurvedicExport = lazy(() => import("@/pages/AyurvedicExport"));
const KoRiceExport = lazy(() => import("@/pages/ko/KoRiceExport"));
const RestaurantSupply = lazy(() => import("@/pages/RestaurantSupply"));
const KoAyurvedaExport = lazy(() => import("@/pages/ko/KoAyurvedaExport"));
const KoRestaurantSupply = lazy(() => import("@/pages/ko/KoRestaurantSupply"));
const KoBlogAssuredCustoms = lazy(() => import("@/pages/ko/KoBlogAssuredCustoms"));
const KoBlogCoupang = lazy(() => import("@/pages/ko/KoBlogCoupang"));
const KoBlogIncoterms = lazy(() => import("@/pages/ko/KoBlogIncoterms"));
const KoBlogHsCodes = lazy(() => import("@/pages/ko/KoBlogHsCodes"));
const KoBlogSampleShipment = lazy(() => import("@/pages/ko/KoBlogSampleShipment"));
const SendToSouthKorea = lazy(() => import("@/pages/SendToSouthKorea"));
const KoreaCustomsHub = lazy(() => import("@/pages/KoreaCustomsHub"));
const WhatYouCanSendHub = lazy(() => import("@/pages/WhatYouCanSendHub"));

const SERVICE_SLUGS = [
  "ddp-shipping", "commercial-shipping", "student-shipping", "proxy-shopping",
  "business-sourcing", "import-export-consulting", "custom-clearance",
  "warehouse", "insurance", "packaging", "excess-luggage", "proxy-shopping-in-india",
  "import-from-south-korea-to-india", "cosmetics-korea-to-india", "excess-baggage-korea-to-india",
];

function PageLoader() {
  return (
    <div className="min-h-[60vh] grid place-items-center">
      <div className="h-10 w-10 rounded-full border-4 border-korea-bluelight border-t-korea-blue animate-spin" />
    </div>
  );
}

function AppRouter() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route element={<Layout />}>
          <Route path="/" element={<Home />} />
          <Route path="/india-to-south-korea-courier" element={<RoutePage direction="india_to_korea" />} />
          <Route path="/south-korea-to-india-courier" element={<RoutePage direction="korea_to_india" />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/book-pickup" element={<GoExternal to={SHIPOMS.book} />} />
          <Route path="/book" element={<GoExternal to={SHIPOMS.book} />} />
          <Route path="/track" element={<GoExternal to={SHIPOMS.track} />} />
          <Route path="/tracking" element={<GoExternal to={SHIPOMS.track} />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/why-shipkorea-vs-ems" element={<WhyShipKoreaVsEms />} />
          <Route path="/trust" element={<Trust />} />
          <Route path="/ecommerce-shipping-india-to-korea" element={<EcommerceShipping />} />
          <Route path="/korean-cosmetics-wholesale-india" element={<KBeautyWholesale />} />
          <Route path="/k-beauty-wholesale-india" element={<Navigate to="/korean-cosmetics-wholesale-india" replace />} />
          <Route path="/cheapest-courier-south-korea-to-india" element={<KoreaToIndiaCheapest />} />
          <Route path="/korea-to-india-ecommerce-sellers" element={<KoreaToIndiaEcommerce />} />
          <Route path="/korea-india-market-entry" element={<KoreaMarketEntry />} />
          <Route path="/north-east-india-south-korea-courier" element={<NorthEastCourier />} />
          <Route path="/courier-from-guwahati-to-south-korea" element={<NECityPage city="guwahati" />} />
          <Route path="/courier-from-imphal-to-south-korea" element={<NECityPage city="imphal" />} />
          <Route path="/courier-from-shillong-to-south-korea" element={<NECityPage city="shillong" />} />
          <Route path="/courier-from-aizawl-to-south-korea" element={<NECityPage city="aizawl" />} />
          <Route path="/courier-from-kohima-to-south-korea" element={<NECityPage city="kohima" />} />
          <Route path="/courier-from-agartala-to-south-korea" element={<NECityPage city="agartala" />} />
          <Route path="/courier-from-gangtok-to-south-korea" element={<NECityPage city="gangtok" />} />
          <Route path="/send-diwali-sweets-to-south-korea" element={<DiwaliSweets />} />
          <Route path="/buy-korean-cosmetics-india" element={<PersonalCosmetics />} />
          <Route path="/indian-rice-export-to-south-korea" element={<IndianRiceExport />} />
          <Route path="/ayurvedic-products-export-to-south-korea" element={<AyurvedicExport />} />
          <Route path="/indian-restaurant-supply-korea" element={<RestaurantSupply />} />
          <Route path="/personal-effects-shipping-india-to-south-korea" element={<PersonalEffects />} />
          <Route path="/send-to-south-korea-from-india" element={<SendToSouthKorea />} />
          <Route path="/south-korea-customs-guide" element={<KoreaCustomsHub />} />
          <Route path="/what-you-can-send-to-south-korea" element={<WhatYouCanSendHub />} />

          {SERVICE_SLUGS.map((s) => (
            <Route key={s} path={`/${s}`} element={<ServicePage />} />
          ))}

          <Route path="/courier-from-india/:slug" element={<CityPage />} />
          <Route path="/courier-to-korea/:slug" element={<KoreaCityPage />} />

          <Route path="/privacy-policy" element={<Legal type="privacy" />} />
          <Route path="/terms" element={<Legal type="terms" />} />
          <Route path="/refund-policy" element={<Legal type="refund" />} />

          <Route path="/404" element={<NotFound />} />
          <Route path="*" element={<NotFound />} />
        </Route>

        {/* Customer auth + dashboard live on the ShipOMS app */}
        <Route path="/signin" element={<GoExternal to={SHIPOMS.login} />} />
        <Route path="/login" element={<GoExternal to={SHIPOMS.login} />} />
        <Route path="/signup" element={<GoExternal to={SHIPOMS.register} />} />
        <Route path="/register" element={<GoExternal to={SHIPOMS.register} />} />
        <Route path="/dashboard" element={<GoExternal to={SHIPOMS.login} />} />

        {/* Korean-language landing (Naver + Google.co.kr SEO) — outside marketing Layout, own chrome */}
        <Route path="/ko" element={<KoreanLanding />} />
        <Route path="/ko/india-market-entry" element={<KoMarketEntry />} />
        <Route path="/ko/ecommerce-india-delivery" element={<KoEcommerceIndia />} />
        <Route path="/ko/blog/korea-to-india-parcel-guide" element={<KoBlogParcelGuide />} />
        <Route path="/ko/blog/kbeauty-india-export-guide" element={<KoBlogKBeautyExport />} />
        <Route path="/ko/indian-rice-export" element={<KoRiceExport />} />
        <Route path="/ko/ayurvedic-products-export" element={<KoAyurvedaExport />} />
        <Route path="/ko/indian-restaurant-supply" element={<KoRestaurantSupply />} />
        <Route path="/ko/blog/assured-customs-clearance" element={<KoBlogAssuredCustoms />} />
        <Route path="/ko/blog/coupang-india-seller-guide" element={<KoBlogCoupang />} />
        <Route path="/ko/blog/incoterms-india-korea-guide" element={<KoBlogIncoterms />} />
        <Route path="/ko/blog/hs-code-india-korea-guide" element={<KoBlogHsCodes />} />
        <Route path="/ko/blog/sample-shipment-korea-buyers" element={<KoBlogSampleShipment />} />
        <Route path="/ko/*" element={<KoreanLanding />} />

        {/* Admin routes — separate chrome */}
        <Route path="/admin/login" element={<GoExternal to={SHIPOMS.login} />} />
        <Route path="/admin" element={<GoExternal to={SHIPOMS.login} />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <HelmetProvider>
      <div className="App">
        <BrowserRouter>
          <Analytics />
          <AppRouter />
        </BrowserRouter>
        <Toaster position="top-center" richColors />
      </div>
    </HelmetProvider>
  );
}

export default App;
